import React, { useMemo } from "react"
import { Box } from "@carrotfertility/carotene-core"
import type { MessagesProviderApi } from "../host/MessagesHostContext"
import { NAV_POLL_INTERVAL_MS, useProviderThreadsQuery } from "../hooks/useProviderThreads"

/**
 * How many provider conversations the member has not read.
 *
 * The transport comes in as a PARAMETER rather than off `useMessagesHost()`, and both callers need
 * that: the nav dot below renders in the app's main nav, far outside `<MessagesHostProvider>`, and the
 * Providers tab's unread badge is rendered by the app's own shell, which is above the panel that
 * mounts the provider. Standing up the host contract for either would be standing it up for a number.
 *
 * `undefined` is the caller declining to ask — the nav and the tab bar for a member who is not a
 * consumer, where provider unread state must not be consulted at all. The query is then disabled and
 * this answers 0, which is not a claim that nothing is unread so much as the absence of a question.
 *
 * The NAV rate, for both callers. It is the slow one because this is mounted for a member's whole
 * session wherever they are in the app, and all it owes them is a count appearing within half a
 * minute. When the Providers tab is open, `useProviderThreads` observes the same cache entry at the
 * faster rate and both surfaces get that — see `useProviderThreadsQuery` for why two timers on one
 * entry is not two requests.
 */
export function useProviderUnreadCount(getThreads: MessagesProviderApi["getThreads"] | undefined): number {
  const { data } = useProviderThreadsQuery(getThreads, NAV_POLL_INTERVAL_MS)
  // `data` is undefined while loading, while disabled and while a data-less query retries, and each
  // of those contributes nothing rather than guessing.
  return useMemo(() => (data?.threads ?? []).filter((thread) => thread.unread).length, [data])
}

// The provider half of the app's nav unread indicator. The app's own dot
// (src/components/views/app/navigation/UnreadMessagesDot.tsx) owns the Carrot half and decides which
// of the two gets to draw, so this renders the same 7px dot and never two.
export function UnreadMessagesDot({
  getThreads
}: {
  /** Absent for a member the nav decided not to ask about; see `useProviderUnreadCount`. */
  getThreads?: MessagesProviderApi["getThreads"]
}): JSX.Element | null {
  const unreadCount = useProviderUnreadCount(getThreads)

  if (unreadCount === 0) {
    return null
  }

  return (
    // Same `data-testid` as the app's own dot, deliberately: the nav renders one or the other and never
    // both, and asserting that is a matter of counting the marks on the page.
    <Box sx={{ display: "flex", justifyContent: "flex-end" }} data-testid="unread-messages-dot">
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
          borderRadius: theme.tokens.borderRadius.round,
          // 7px is not a spacing step — the tokens either side are `xxs` (4px) and `xs` (8px) — and the
          // size is not ours to round off: the app's own dot draws 7px, the nav renders one or the
          // other, and a token here would give the member a dot that changed size with its source.
          blockSize: "7px",
          inlineSize: "7px"
        })}
      />
    </Box>
  )
}
